import React, { useState, useEffect, useCallback } from "react";
import {
  ActionList,
  Box,
  IconButton,
  Text,
  RelativeTime,
  Truncate,
} from "@primer/react";
import { DownloadIcon } from "@primer/octicons-react";
import railsFetch from "../railsFetch";
import { ReportExport } from "./types";
import { DownloadConfirmationDialog } from "./DownloadConfirmationDialog";
import { triggerDownload } from "./utils";
import {
  useTrayDropdown,
  StatusIcon,
  StatusIndicator,
  TrayDropdown,
  ViewAllLink,
  formatShortDateTime,
  isExpired,
  isActive,
  isNewComplete,
} from "../TrayShared";

export interface DownloadTrayProps {
  eventId: string;
  reportExportsUrl: string;
  downloadsPageUrl: string;
}

export const DownloadTray: React.FC<DownloadTrayProps> = ({
  eventId,
  reportExportsUrl,
  downloadsPageUrl,
}) => {
  const [exports, setExports] = useState<ReportExport[]>([]);
  const [hasActiveDownloads, setHasActiveDownloads] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { isOpen, setIsOpen, menuRef, buttonRef } = useTrayDropdown();
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    exportType: string;
  }>({ isOpen: false, exportType: "" });

  const fetchExports = useCallback(async () => {
    try {
      const response = await railsFetch(
        `${reportExportsUrl}.json`,
        { method: "GET" }
      );

      const data: ReportExport[] = await response.json();
      const validExports = data.filter((e) => !isExpired(e));

      setExports(validExports);
      setHasActiveDownloads(validExports.filter((e) => isActive(e)).length > 0);
      setHasLoaded(true);
    } catch (error) {
      console.error("[DownloadTray] Failed to fetch exports:", error);
      setHasLoaded(true);
    }
  }, [reportExportsUrl]);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  // Poll for status updates when there are active downloads
  useEffect(() => {
    if (!hasActiveDownloads) return;

    const interval = setInterval(fetchExports, 3000);
    return () => clearInterval(interval);
  }, [hasActiveDownloads, fetchExports]);

  // Listen for new export events
  useEffect(() => {
    const handleExportStarted = () => fetchExports();
    window.addEventListener("reportExportStarted", handleExportStarted);
    return () => window.removeEventListener("reportExportStarted", handleExportStarted);
  }, [fetchExports]);

  // Listen for download complete events
  useEffect(() => {
    const handleDownloadComplete = () => fetchExports();
    window.addEventListener("downloadComplete", handleDownloadComplete);
    return () => window.removeEventListener("downloadComplete", handleDownloadComplete);
  }, [fetchExports]);

  // Listen for download confirmation dialog requests
  useEffect(() => {
    const handleShowConfirmation = (event: CustomEvent<{ exportType: string }>) => {
      setIsOpen(false);
      setConfirmationDialog({
        isOpen: true,
        exportType: event.detail.exportType,
      });
    };

    window.addEventListener(
      "showDownloadConfirmation",
      handleShowConfirmation as EventListener
    );

    return () => {
      window.removeEventListener(
        "showDownloadConfirmation",
        handleShowConfirmation as EventListener
      );
    };
  }, []);

  const handleDownload = (exportItem: ReportExport) => {
    setIsOpen(false);
    if (exportItem.downloadUrl) {
      triggerDownload(exportItem.downloadUrl, exportItem.filename);
    }
  };

  const getExportLabel = (exp: ReportExport): string => {
    const name = exp.reportName || exp.gridType;
    const format = exp.exportType.toUpperCase();
    return `Report / ${name} ${format}`;
  };

  const hasExports = hasLoaded && exports.length > 0;
  const hasProcessing = exports.some((e) => e.status === "processing");
  const hasNewComplete = exports.some((e) => isNewComplete(e));

  return (
    <>
      <DownloadConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        exportType={confirmationDialog.exportType}
        onClose={() => setConfirmationDialog({ isOpen: false, exportType: "" })}
      />
      {hasExports && (
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <IconButton
              ref={buttonRef}
              icon={DownloadIcon}
              aria-label="Downloads"
              aria-expanded={isOpen}
              aria-haspopup="menu"
              size="small"
              variant="invisible"
              onClick={() => setIsOpen(!isOpen)}
            />
            <StatusIndicator
              hasProcessing={hasProcessing}
              hasNewComplete={hasNewComplete}
            />
          </Box>

          {isOpen && (
            <TrayDropdown ref={menuRef}>
              <ActionList>
                <ActionList.Group title="Downloads">
                  {exports.map((exp) => {
                    const isComplete = exp.status === "complete";
                    const isFailed = exp.status === "failed";

                    return (
                      <ActionList.Item
                        key={`export-${exp.id}`}
                        onSelect={() => {
                          setIsOpen(false);
                          if (isComplete && exp.downloadUrl) {
                            handleDownload(exp);
                          }
                        }}
                        disabled={!isComplete || !exp.downloadUrl}
                      >
                        <ActionList.LeadingVisual>
                          <StatusIcon status={exp.status} isNew={isNewComplete(exp)} />
                        </ActionList.LeadingVisual>
                        <Box sx={{ flex: 0, minWidth: 0 }}>
                          <Truncate title={getExportLabel(exp)} maxWidth="100%" sx={{ fontSize: 0 }}>
                            {getExportLabel(exp)}
                          </Truncate>
                          {isComplete && exp.downloadUrl && (
                            <Text sx={{ fontSize: "10px", color: "fg.muted", display: "block" }}>
                              <RelativeTime date={new Date(exp.createdAt)} />
                            </Text>
                          )}
                          {exp.status === "pending" && exp.createdAt && (
                            <Text sx={{ fontSize: "10px", color: "fg.subtle", display: "block" }}>
                              Queued... {formatShortDateTime(exp.createdAt, exp.timezone)}
                            </Text>
                          )}
                          {exp.status === "processing" && exp.createdAt && (
                            <Text sx={{ fontSize: "10px", color: "fg.subtle", display: "block" }}>
                              Processing... started {formatShortDateTime(exp.createdAt, exp.timezone)}
                            </Text>
                          )}
                          {isFailed && exp.errorMessage && (
                            <Truncate
                              title={exp.errorMessage}
                              maxWidth="100%"
                              style={{ fontSize: "10px", color: "var(--fgColor-danger, #d1242f)", display: "block" }}
                            >
                              {exp.errorMessage}
                            </Truncate>
                          )}
                        </Box>
                        <ActionList.TrailingVisual>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {isComplete && exp.downloadUrl && (
                              <IconButton
                                icon={DownloadIcon}
                                size="small"
                                variant="invisible"
                                aria-label="Download"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsOpen(false);
                                  handleDownload(exp);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            )}
                          </Box>
                        </ActionList.TrailingVisual>
                      </ActionList.Item>
                    );
                  })}
                </ActionList.Group>
                <ActionList.Divider />
                <ViewAllLink href={downloadsPageUrl} label="View All Downloads" />
              </ActionList>
            </TrayDropdown>
          )}
        </Box>
      )}
    </>
  );
};

export default DownloadTray;
